import "./new-quotation.scss";

import HeaderContent from "@shared/ui/header-content/header-content";
import { Button } from "primereact/button";
import { useMemo, useRef } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { FaChevronLeft } from "react-icons/fa6";

import { CreateQuoteTransportChildrenDto } from "../../../../api";
import { currencyFormat } from "../../../shared/utility/formats/currency-format";
import PreviewFile from "../../preview-file/preview-file";
import FormQuotation from "../form-quotation/form-quotation";
import QuoteAccommodation from "../form-quotation/quote-accommodation";
import QuoteActivities from "../form-quotation/quote-activities";
import QuoteTransport from "../form-quotation/quote-transport";
import { useNewQuotation } from "./new-quotation.hook";

export function NewQuotation() {
  const {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  } = useNewQuotation();
  const fileRef = useRef<HTMLDivElement>(null);

  const { control, setValue } = formMethod;

  const watchedFields = useWatch({
    control,
    name: [
      "activities",
      "transport",
      "accommodation",
      "agentMarkup",
    ],
  });

  const totalAmount = useMemo(() => {
    const [activities, transport, accommodation, agentMarkup] =
      watchedFields;

    const sumArray = (arr: any, calculateChildRate = false) => {
      if (!Array.isArray(arr)) return 0;
      if (calculateChildRate) {
        return arr.reduce((sum, item) => sum + (item?.childRate || 0), 0);
      }
      return arr.reduce((sum, item) => sum + (item?.amount || 0), 0);
    };
    const sumAccommodation = (arr: any) => {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce(
        (sum, item) => sum + item.price,
        0
      );
    };

    const sumChildTransport = (arr: any) => {
      if (!Array.isArray(arr)) return 0;
      return arr.reduce((sum, item) => sum + (item?.amount || 0), 0)
    }

    let childSum = 0;
    let adultSum = 0;
    let sum = 0;
    if (activities) {
      adultSum += sumArray(activities);
      childSum += sumArray(activities, true);
    }
    if (transport) {
      adultSum += sumArray(transport);
      let childTransports: any[] = []
      transport.forEach((item) => {
        if (item.childPrices) {
          childTransports = [...childTransports, ...(item.childPrices)]
        }
      })
      childSum += sumChildTransport(childTransports)
    }
    if (accommodation) {
      adultSum += sumAccommodation(accommodation);
    }
    if (agentMarkup) {
      sum = (childSum + adultSum) * (1 + agentMarkup / 100);
    }
    else {
      sum = (childSum + adultSum);
    }
    setValue("childRateTotal", childSum);
    setValue("adultRateTotal", adultSum);
    setValue("amount", sum);
    return sum;
  }, [watchedFields]);

  return (
    <div id="NewQuotation" className="quotation">
      <HeaderContent
        title={<h1>New Quotation</h1>}
        backIcon={<FaChevronLeft />}
        onBack={() => navigate("../")}
      >
        <Button
          className="w-full block"
          label="Save"
          disabled={isCreating}
          onClick={handleSubmit(handleValidate, handleValidateError)}
        ></Button>
      </HeaderContent>

      <div className="p-7">
        <FormProvider {...formMethod}>
          <div className="flex justify-end">
            <span className="text-2xl font-bold">
              {totalAmount
                ? `Total: ${currencyFormat(totalAmount)}`
                : `Total: ${currencyFormat(0)}`}
            </span>
          </div>
          <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            <div>
              <FormQuotation />
              <QuoteTransport />
              <QuoteAccommodation />
              <QuoteActivities />
            </div>
            <div>
              <PreviewFile fileRef={fileRef} />
            </div>
          </section>
        </FormProvider>
      </div>
    </div>
  );
}

export default NewQuotation;
